import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Target, Brain, Download, MessageSquare, Lightbulb, Flag, CheckSquare, X, AlertTriangle, Share, Loader2 } from "lucide-react";
import { Analysis } from "../types";
import { ShareDropdown } from "./ShareDropdown";
import { useState, useRef, useEffect } from "react";
import { extractShareIdFromPath, extractShareIdFromHash } from "../utils/routeUtils";
import { useTranslation } from "react-i18next";

interface ActionStep {
  id: string;
  text: string;
  priority: 1 | 2 | 3;
  timeframe: string;
}

interface RecommendationV3Props {
  mainRecommendation: string;
  shortRecommendation?: string;
  keyFactors?: string[];
  actionSteps?: ActionStep[] | string[];
  finalDecision?: 'YES' | 'NO' | 'MAYBE';
  originalDecision?: 'YES' | 'NO' | 'MAYBE'; // Исходный вердикт от ИИ (не изменяется при переоценке)
  confidence: number;
  theme: 'dark' | 'light';
  language: 'ru' | 'en';
  // Пропсы для шеринга
  analysis?: Analysis;
  onShare?: (analysis?: Analysis) => Promise<string | null>;
  onExportPDF?: (analysis?: Analysis) => Promise<void>;
  isSharing?: boolean;
  isExportingPDF?: boolean;
  shareUrl?: string | null;
  hasBeenShared?: boolean;
  shareError?: string | null;
  // Пропс для нового вопроса
  onNewQuestion?: () => void;
  // Пропс для shareId (для расшаренных страниц)
  shareId?: string;
  // Пропсы для анимации
  isTransitioning?: boolean;
  transitionDelay?: number;
}

export function RecommendationV3({ 
  mainRecommendation, 
  shortRecommendation, 
  keyFactors: _keyFactors = [],
  actionSteps = [],
  finalDecision,
  originalDecision,
  confidence,
  theme,
  language,
  // Пропсы для шеринга
  analysis,
  onShare,
  onExportPDF,
  isSharing = false,
  isExportingPDF = false,
  shareUrl,
  hasBeenShared = false,
  shareError,
  // Пропс для нового вопроса
  onNewQuestion,
  // Пропс для shareId
  shareId,
  // Пропсы для анимации
  isTransitioning = false,
  transitionDelay = 0
}: RecommendationV3Props) {
  const { t } = useTranslation('analysis');
  const isDark = theme === 'dark';
  const decisionChanged =
    originalDecision !== undefined &&
    finalDecision !== undefined &&
    originalDecision !== finalDecision;

  const [isShareDropdownOpen, setIsShareDropdownOpen] = useState(false);
  const shareButtonRef = useRef<HTMLButtonElement>(null);
  const [isRecommendationCollapsed, setIsRecommendationCollapsed] = useState(false);
  const bannerRef = useRef<HTMLImageElement>(null);
  const [bannerLoaded, setBannerLoaded] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Функция для получения shareId (из shareUrl или из пропсов)
  const getShareIdForBanner = (): string | null => {
    // Сначала пытаемся извлечь из shareUrl
    if (shareUrl) {
      try {
        let shareIdWithSlug: string | null = null;
        
        if (shareUrl.includes('/share/')) {
          try {
            const url = new URL(shareUrl);
            shareIdWithSlug = extractShareIdFromPath(url.pathname);
          } catch {
            shareIdWithSlug = extractShareIdFromPath(shareUrl);
          }
        } else {
          shareIdWithSlug = extractShareIdFromHash(shareUrl);
        }
        
        if (shareIdWithSlug) {
          return shareIdWithSlug;
        }
      } catch (error) {
        console.error('Error extracting shareId from shareUrl:', error);
      }
    }
    
    // Если не удалось извлечь из shareUrl, используем shareId из пропсов
    if (shareId) {
      return shareId;
    }
    
    return null;
  };

  // Функция для получения URL изображения баннера
  const getBannerImageUrl = (): string | null => {
    const shareIdForBanner = getShareIdForBanner();
    
    if (!shareIdForBanner) {
      return null;
    }
    
    const SHARE_SERVER_URL = import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002';
    return `${SHARE_SERVER_URL}/api/image/${shareIdForBanner}?type=verdict`;
  };

  // Получаем URL изображения баннера
  const bannerImageUrl = getBannerImageUrl();
  
  // Формируем shareUrl для кнопки "Поделиться", если его нет
  const shareUrlForButton = shareUrl || (shareId ? `${window.location.origin}/share/${shareId}` : null);

  // Если вердикт изменился, по умолчанию сворачиваем блок рекомендаций
  useEffect(() => {
    if (decisionChanged) {
      setIsRecommendationCollapsed(true);
    }
  }, [decisionChanged]);


  // Lazy loading для баннера через Intersection Observer
  useEffect(() => {
    const banner = bannerRef.current;
    if (!banner || !bannerImageUrl) {
      setBannerLoaded(false);
      return;
    }

    // Сбрасываем состояние загрузки при изменении URL
    setBannerLoaded(false);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Загружаем изображение только когда оно видно
              banner.src = bannerImageUrl;
              observer.unobserve(banner);
            }
          });
        },
        {
          rootMargin: '50px', // Начинаем загрузку за 50px до появления в viewport
        }
      );

      observer.observe(banner);

      return () => {
        observer.disconnect();
      };
    } else {
      // Fallback для браузеров без Intersection Observer
      banner.src = bannerImageUrl;
    }
  }, [bannerImageUrl]);

  // Обработчик копирования ссылки
  const handleCopyShareLink = async () => {
    if (!shareUrlForButton) return;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrlForButton);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } else {
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = shareUrlForButton;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          setLinkCopied(true);
          setTimeout(() => setLinkCopied(false), 2000);
        }
      }
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  // Обработчик экспорта PDF
  const handleExportPDF = async () => {
    try {
      if (onExportPDF) {
        await onExportPDF(analysis);
      }
    } catch (error) {
      console.error('Error in PDF export:', error);
    }
  };

  // Обработчик жалобы
  const handleReportIssue = () => {
    const subject = language === 'ru' 
      ? 'Жалоба на анализ' 
      : 'Analysis Report Issue';
    
    // Добавляем shareId в текст письма, если он есть (для расшаренных страниц)
    const shareIdText = shareId ? `\n- Share ID: ${shareId}` : '';
    
    const body = language === 'ru'
      ? `Здравствуйте!\n\nЯ хочу сообщить о проблеме с анализом:\n\n- ID анализа: ${analysis?.id || 'неизвестно'}\n- Внешний ID: ${analysis?.external_id || 'неизвестно'}\n- Заголовок: ${analysis?.title || 'неизвестно'}${shareIdText}\n\nОписание проблемы:\n\n\n\nС уважением`
      : `Hello!\n\nI would like to report an issue with the analysis:\n\n- Analysis ID: ${analysis?.id || 'unknown'}\n- External ID: ${analysis?.external_id || 'unknown'}\n- Title: ${analysis?.title || 'unknown'}${shareIdText}\n\nProblem description:\n\n\n\nBest regards`;

    const mailtoLink = `mailto:help@plinus.ru?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, '_blank', 'noopener,noreferrer');
  };

  // Обработчик нажатия кнопки "Поделиться"
  const handleShareClick = async () => {
    if (isSharing) return; // Защита от повторных нажатий
    
    // Если dropdown уже открыт, просто закрываем его
    if (isShareDropdownOpen) {
      setIsShareDropdownOpen(false);
      return;
    }
    
    // Если уже был шеринг и нет изменений, просто открываем dropdown
    if (hasBeenShared && analysis && !analysis.isModified) {
      setIsShareDropdownOpen(true);
      return;
    }
    
    // Если есть функция onShare, вызываем её для создания шеринга
    if (onShare) {
      try {
        await onShare(analysis);
        // После успешного шеринга открываем dropdown
        setIsShareDropdownOpen(true);
      } catch (error) {
        console.error('Ошибка при создании шеринга:', error);
        // В случае ошибки не открываем dropdown
      }
    } else {
      // Если нет функции onShare, просто открываем dropdown
      setIsShareDropdownOpen(true);
    }
  };
  
  return (
    <div className="space-y-8 w-full max-w-full">
      <div className="text-center space-y-4">
        <h2 className={`text-3xl heading-section ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>{t('finalRecommendations')}</h2>
        <p className={`text-xl max-w-2xl mx-auto ${
          isDark ? 'text-slate-300' : 'text-gray-700'
        }`}>
          {t('conclusionSubtitle')}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 w-full max-w-full">
        {/* Main recommendation */}
        <div className="lg:col-span-2 space-y-6 min-w-0">
          <Card className={`relative bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-indigo-600/10 border-0 p-4 sm:p-6 lg:p-8 min-w-0 overflow-hidden ${
            !isDark && 'shadow-xl'
          }`}>
            {/* Background glow */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 via-purple-500 to-indigo-600" />
            
            <div className="relative space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-purple-600/20 rounded-2xl flex items-center justify-center border border-blue-500/30">
                  <Target className="w-8 h-8 text-blue-400" />
                </div>
                <div>
                  <h3 className={`text-2xl font-semibold mb-1 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('mainRecommendation')}
                  </h3>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                      <Brain className="w-3 h-3 mr-1" />
                      {t('aiAnalysis')}
                    </Badge>
                    <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                      {confidence}% {t('accuracy')}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                {decisionChanged && (
                  <Card
                    className={`p-4 border-l-4 ${
                      isDark
                        ? 'bg-amber-900/20 border-amber-500/60'
                        : 'bg-amber-50 border-amber-400'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle
                        className={`w-5 h-5 mt-0.5 ${
                          isDark ? 'text-amber-300' : 'text-amber-500'
                        }`}
                      />
                      <div>
                        <p
                          className={`text-sm font-medium ${
                            isDark ? 'text-amber-100' : 'text-amber-900'
                          }`}
                        >
                          {language === 'ru'
                            ? 'Вердикт был изменён — исходные рекомендации утратили силу.'
                            : 'The decision has changed — the original recommendations are no longer valid.'}
                        </p>
                        <p
                          className={`mt-1 text-xs ${
                            isDark ? 'text-amber-200' : 'text-amber-800'
                          }`}
                        >
                          {language === 'ru'
                            ? 'Ориентируйтесь на обновлённый список аргументов — текущие рекомендации могут не учитывать внесённые изменения.'
                            : 'Rely on the updated list of arguments — the current recommendations may not reflect your recent changes.'}
                        </p>

                        <button
                          type="button"
                          onClick={() =>
                            setIsRecommendationCollapsed(prev => !prev)
                          }
                          className={`mt-3 text-xs font-medium underline-offset-2 transition ${
                            isDark
                              ? 'text-amber-200 hover:text-amber-100'
                              : 'text-amber-800 hover:text-amber-900'
                          }`}
                        >
                          {/* Пунктирное подчёркивание, чтобы визуально подсветить кликабельность */}
                          <span className="underline decoration-dotted">
                            {isRecommendationCollapsed
                            ? language === 'ru'
                              ? 'Показать исходные рекомендации'
                              : 'Show original recommendations'
                            : language === 'ru'
                            ? 'Скрыть исходные рекомендации'
                            : 'Hide original recommendations'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </Card>
                )}

                {(!decisionChanged || !isRecommendationCollapsed) && (
                  <>
                    <p
                      className={`text-lg leading-relaxed break-words ${
                        isDark ? 'text-slate-200' : 'text-gray-800'
                      }`}
                    >
                      {mainRecommendation}
                    </p>
                    
                    {shortRecommendation && (
                      <Card
                        className={`p-4 ${
                          isDark
                            ? 'bg-slate-800/50 border-slate-700/50'
                            : 'bg-white/80 border-gray-200/60 shadow-lg'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Lightbulb className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4
                              className={`font-semibold mb-2 ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}
                            >
                              {language === 'ru' ? 'Краткий вывод' : 'Summary'}
                            </h4>
                            <p
                              className={`break-words ${
                                isDark ? 'text-slate-300' : 'text-gray-700'
                              }`}
                            >
                              {shortRecommendation}
                            </p>
                          </div>
                        </div>
                      </Card>
                    )}

                    {/* Action Plan / Alternatives */}
                    {actionSteps.length > 0 && (
                      <Card
                        className={`p-5 ${
                          isDark
                            ? 'bg-slate-800/50 border-slate-700/50'
                            : 'bg-white/80 border-gray-200/60 shadow-lg'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-5">
                          {/* Используем originalDecision для определения заголовка, чтобы он не менялся при переоценке */}
                          {(originalDecision ?? finalDecision) === 'NO' ? (
                            <AlertTriangle className="w-5 h-5 text-orange-400" />
                          ) : (
                            <CheckSquare className="w-5 h-5 text-green-400" />
                          )}
                          <h4
                            className={`font-semibold ${
                              isDark ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {(originalDecision ?? finalDecision) === 'NO'
                              ? language === 'ru'
                                ? 'Альтернативы и снижение рисков'
                                : 'Alternatives & Risk Mitigation'
                              : language === 'ru'
                              ? 'План действий'
                              : 'Action Plan'}
                          </h4>
                        </div>
                        
                        <div className="space-y-4">
                          {actionSteps.map((step, index) => {
                            // Проверяем, является ли step строкой или объектом
                            const isString = typeof step === 'string';
                            const stepData = isString
                              ? {
                                  id: `step-${index}`,
                                  text: step,
                                  priority: 2,
                                  timeframe: ''
                                }
                              : (step as any); // Используем any для гибкости с разными форматами
                            
                            return (
                              <div
                                key={stepData.id || `step-${index}`}
                                className="flex gap-4"
                              >
                                <div
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center font-semibold flex-shrink-0 ${
                                    stepData.priority === 1
                                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/20'
                                      : stepData.priority === 2
                                      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/20'
                                      : 'bg-green-500/20 text-green-400 border border-green-500/20'
                                  }`}
                                >
                                  {index + 1}
                                </div>
                                <div className="flex-1 space-y-2">
                                  <p
                                    className={`leading-relaxed break-words ${
                                      isDark
                                        ? 'text-slate-200'
                                        : 'text-gray-800'
                                    }`}
                                  >
                                    {stepData.step || stepData.text}
                                  </p>
                                  {(stepData.timeline ||
                                    stepData.timeframe) && (
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                      <span
                                        className={`text-xs break-words ${
                                          isDark
                                            ? 'text-slate-400'
                                            : 'text-gray-500'
                                        }`}
                                      >
                                        {stepData.timeline ||
                                          stepData.timeframe}
                                      </span>
                                      <div
                                        className={`hidden sm:block w-1 h-1 rounded-full ${
                                          isDark
                                            ? 'bg-slate-600'
                                            : 'bg-gray-300'
                                        }`}
                                      />
                                      {stepData.resources && (
                                        <span
                                          className={`text-xs break-words ${
                                            isDark
                                              ? 'text-slate-400'
                                              : 'text-gray-500'
                                          }`}
                                        >
                                          {stepData.resources}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    )}
                  </>
                )}
              </div>
            </div>
          </Card>

        </div>

        {/* Sidebar */}
        <div className="space-y-6 min-w-0">
          {/* Quality metrics */}
          <Card className={`p-4 sm:p-6 ${
            isDark 
              ? 'bg-slate-800/30 border-slate-700/50' 
              : 'bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50 border-blue-200/60 shadow-xl'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              <Brain className={`w-5 h-5 ${
                isDark ? 'text-purple-400' : 'text-purple-600'
              }`} />
              {t('analysisQuality')}
            </h3>
            
            <div className="space-y-4">
              {[
                { 
                  label: t('reliability'), 
                  value: confidence, 
                  color: 'blue' 
                },
                { 
                  label: language === 'ru' ? 'Полнота данных' : 'Data Completeness', 
                  value: 95, 
                  color: 'green' 
                },
                { 
                  label: t('objectivity'), 
                  value: 88, 
                  color: 'purple' 
                }
              ].map((metric, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${
                      isDark ? 'text-slate-300' : 'text-gray-600'
                    }`}>{metric.label}</span>
                    <span className={`text-sm font-semibold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>{metric.value}%</span>
                  </div>
                  <div className={`w-full h-2 rounded-full overflow-hidden ${
                    isDark ? 'bg-slate-700' : 'bg-gray-200'
                  }`}>
                    <div 
                      className={`h-full rounded-full transition-all duration-1000 ease-out bg-gradient-to-r ${
                        metric.color === 'blue' ? 
                          (isDark ? 'from-blue-400 to-blue-600' : 'from-blue-500 to-blue-700') :
                        metric.color === 'green' ? 
                          (isDark ? 'from-green-400 to-green-600' : 'from-emerald-500 to-green-600') :
                          (isDark ? 'from-purple-400 to-purple-600' : 'from-purple-500 to-purple-700')
                      }`}
                      style={{ width: `${metric.value}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Actions */}
          <Card className={`p-4 sm:p-6 ${ 
            isDark 
              ? 'bg-slate-800/30 border-slate-700/50' 
              : 'bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 border-indigo-200/60 shadow-xl'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>{t('actions')}</h3>
            
            {/* Отображение ошибки экспорта */}
            {shareError && (
              <div className={`mb-4 p-3 rounded-lg border-l-4 ${
                isDark ? 'bg-red-900/20 border-red-800/30' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'
                  }`}>
                    <X className="w-3 h-3" />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-red-300' : 'text-red-800'}`}>
                      {t('exportError')}
                    </p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                      {shareError}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              {/* Кнопка "Экспорт отчёта (.pdf)" - показываем только если onExportPDF передан */}
              {onExportPDF && (
                <Button 
                  variant="outline" 
                  onClick={handleExportPDF}
                  disabled={isSharing || isExportingPDF}
                  className={`w-full justify-start transition-all duration-200 ${
                    isDark 
                      ? 'border-slate-600 bg-slate-800/50 text-slate-300 hover:text-white hover:bg-slate-700/70 hover:border-slate-500'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 shadow-sm hover:shadow-md'
                  } ${(isSharing || isExportingPDF) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center mr-3 transition-colors duration-200 ${
                    isDark 
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-blue-500/10 text-blue-600'
                  }`}>
                    {(isSharing || isExportingPDF) ? (
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </div>
                  {isSharing 
                    ? t('creatingLink')
                    : isExportingPDF
                    ? t('downloading')
                    : t('exportReportPdf')
                  }
                </Button>
              )}
              <Button 
                variant="outline" 
                onClick={onNewQuestion}
                className={`w-full justify-start transition-all duration-200 ${
                  isDark 
                    ? 'border-slate-600 bg-slate-800/50 text-slate-300 hover:text-white hover:bg-slate-700/70 hover:border-slate-500'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 shadow-sm hover:shadow-md'
                }`}
              >
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center mr-3 transition-colors duration-200 ${
                  isDark 
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'bg-purple-500/10 text-purple-600'
                }`}>
                  <MessageSquare className="w-4 h-4" />
                </div>
                {t('newAIQuestion')}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleReportIssue}
                className={`w-full justify-start transition-all duration-200 ${
                  isDark 
                    ? 'border-slate-600 bg-slate-800/50 text-slate-300 hover:text-white hover:bg-slate-700/70 hover:border-slate-500'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 shadow-sm hover:shadow-md'
                }`}
              >
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center mr-3 transition-colors duration-200 ${
                  isDark 
                    ? 'bg-red-500/20 text-red-400'
                    : 'bg-red-500/10 text-red-600'
                }`}>
                  <Flag className="w-4 h-4" />
                </div>
                {t('reportIssue')}
              </Button>

              {/* Баннер и кнопка "Поделиться" */}
              {(bannerImageUrl || shareUrlForButton) && (
                <div className={`p-3 rounded-lg border ${
                isDark 
                  ? 'border-slate-700/30 bg-slate-800/10' 
                  : 'border-gray-200/50 bg-gray-50/30'
              }`}>
                <div className="flex flex-col items-center gap-3">
                  {bannerImageUrl && (
                    <div className="relative w-full aspect-[1000/525]">
                      {/* Placeholder */}
                      {!bannerLoaded && (
                        <div className={`absolute inset-0 flex items-center justify-center rounded-lg ${
                          isDark ? 'bg-slate-700/50' : 'bg-gray-100'
                        }`}>
                          <span className={`text-sm ${
                            isDark ? 'text-slate-400' : 'text-gray-500'
                          }`}>
                            {t('loadingImage')}
                          </span>
                        </div>
                      )}
                      {/* Баннер */}
                      <img
                        ref={bannerRef}
                        data-src={bannerImageUrl}
                        alt={analysis?.title || analysis?.description || ''}
                        className={`w-full h-full object-cover rounded-lg shadow-md transition-opacity duration-300 ${
                          bannerLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => setBannerLoaded(true)}
                        onError={() => {
                          setBannerLoaded(false);
                        }}
                        loading="lazy"
                      />
                    </div>
                  )}
                  {/* Кнопка "Поделиться" */}
                  <Button
                    variant="outline"
                    onClick={handleCopyShareLink}
                    className={`w-full justify-center transition-all duration-200 ${
                      linkCopied
                        ? isDark
                          ? 'border-green-600 bg-green-900/20 text-green-300'
                          : 'border-green-500 bg-green-50 text-green-700'
                        : isDark
                        ? 'border-slate-600 bg-slate-800/50 text-slate-300 hover:text-white hover:bg-slate-700/70 hover:border-slate-500'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <Share className="w-4 h-4 mr-2" />
                    {linkCopied
                      ? t('linkCopiedToast')
                      : t('share')}
                  </Button>
                </div>
              </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Плавающая кнопка "Поделиться" для мобильной версии */}
      {onShare && (
        <>
          <div className="lg:hidden fixed bottom-6 right-2 sm:right-6 z-50">
            <Button
              ref={shareButtonRef}
              variant="default"
              size="lg"
              onClick={handleShareClick}
              disabled={isSharing}
              className={`relative group shadow-2xl ${
                isDark 
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white' 
                  : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white'
              } ${isShareDropdownOpen ? 'ring-2 ring-purple-400/50' : ''} font-medium transition-all duration-200 px-6 py-4 rounded-full ${
                isSharing ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isSharing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Share className="w-5 h-5" />
              )}
            </Button>
          </div>

          <ShareDropdown
            isOpen={isShareDropdownOpen}
            onClose={() => setIsShareDropdownOpen(false)}
            language={language}
            theme={theme}
            analysisTitle={analysis?.description || analysis?.title || ""}
            triggerRef={shareButtonRef}
            shareUrl={shareUrl}
            hasBeenShared={hasBeenShared}
            onExportPDF={onExportPDF}
            isExportingPDF={isExportingPDF}
            shareError={shareError}
            shareId={shareId}
            externalId={analysis?.external_id}
          />
        </>
      )}
    </div>
  );
}
